import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Wrench, AlertTriangle, HardHat, Mail, Plus, Upload } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/lib/helpers";
import { IssueExtras, ContractorPicker } from "@/components/PropertyFinance";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

function ContractorDialog({ issue, onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ contractor_name: "", contractor_email: "", note: "" });
  const submit = async () => {
    try {
      const { data } = await api.post(`/issues/${issue.id}/assign-contractor`, form);
      toast.success(data.emailed ? `Job emailed to ${form.contractor_email}` : "Assigned (email not sent — check address)");
      setOpen(false);
      onDone();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline" className="w-full" data-testid={`assign-contractor-${issue.id}`}><HardHat className="h-3.5 w-3.5 mr-1" />Assign Contractor</Button></DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-head">Assign Contractor</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">Emails the job details for <strong>{issue.title}</strong> and marks it In Progress.</p>
          <ContractorPicker onPick={(c) => setForm({ ...form, contractor_name: c.name, contractor_email: c.email })} />
          <div><Label className="overline">Contractor name</Label><Input data-testid="contractor-name-input" value={form.contractor_name} onChange={(e) => setForm({ ...form, contractor_name: e.target.value })} className="mt-1.5" /></div>
          <div><Label className="overline">Contractor email</Label><Input data-testid="contractor-email-input" value={form.contractor_email} onChange={(e) => setForm({ ...form, contractor_email: e.target.value })} className="mt-1.5" /></div>
          <div><Label className="overline">Note (optional)</Label><Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-1.5" /></div>
        </div>
        <DialogFooter><Button data-testid="send-contractor-button" onClick={submit} disabled={!form.contractor_name || !form.contractor_email}><Mail className="h-4 w-4 mr-1.5" />Assign & Email</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const CATEGORIES = ["Plumbing", "Electrical", "Heating", "Appliance", "Structural", "Pest", "Other"];
const EMPTY_ISSUE = { property_id: "", title: "", description: "", category: "Other", priority: "medium", photos: [] };

function NewIssueDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [props, setProps] = useState([]);
  const [form, setForm] = useState(EMPTY_ISSUE);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) api.get("/properties").then((r) => setProps(r.data)).catch(() => {}); }, [open]);
  const onPhoto = (e) => {
    const files = Array.from(e.target.files || []);
    Promise.all(files.map((f) => new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(f); })))
      .then((arr) => setForm((p) => ({ ...p, photos: [...p.photos, ...arr].slice(0, 6) })));
  };
  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/issues", form);
      toast.success("Issue logged");
      setOpen(false); setForm(EMPTY_ISSUE); onCreated();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setForm(EMPTY_ISSUE); }}>
      <DialogTrigger asChild><Button data-testid="new-issue-button"><Plus className="h-4 w-4 mr-1.5" />New Issue</Button></DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-head">Log a repair issue</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="overline">Property</Label>
            <Select value={form.property_id} onValueChange={(v) => setForm({ ...form, property_id: v })}>
              <SelectTrigger data-testid="new-issue-property" className="mt-1.5"><SelectValue placeholder="Select a property" /></SelectTrigger>
              <SelectContent>{props.map((p) => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="overline">Title</Label><Input data-testid="new-issue-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5" placeholder="e.g. Boiler not firing" /></div>
          <div><Label className="overline">Description</Label><Textarea data-testid="new-issue-description" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" placeholder="What's wrong, and where in the property?" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="overline">Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="new-issue-category" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline">Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger data-testid="new-issue-priority" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="flex items-center gap-2 text-sm underline cursor-pointer"><Upload className="h-4 w-4" />Add photos (optional)<input type="file" accept="image/*" multiple className="hidden" data-testid="new-issue-photos" onChange={onPhoto} /></label>
            {form.photos.length > 0 && <div className="flex gap-2 flex-wrap mt-2">{form.photos.map((p, i) => <img key={i} src={p} alt="" className="h-16 w-16 object-cover rounded-md border border-border" />)}</div>}
          </div>
        </div>
        <DialogFooter><Button data-testid="new-issue-submit" onClick={submit} disabled={saving || !form.property_id || !form.title.trim() || !form.description.trim()}><Wrench className="h-4 w-4 mr-1.5" />Log Issue</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const STATUS_META = {
  new: { label: "New", cls: "bg-[#3a4a6b]/12 text-[#3a4a6b]" },
  in_progress: { label: "In Progress", cls: "bg-[#D98324]/12 text-[#D98324]" },
  resolved: { label: "Resolved", cls: "bg-[#2B4C3F]/12 text-[#2B4C3F]" },
};
const PRIORITY_CLS = {
  urgent: "bg-[#9E382A]/12 text-[#9E382A]", high: "bg-[#D98324]/12 text-[#D98324]",
  medium: "bg-secondary text-muted-foreground", low: "bg-secondary text-muted-foreground",
};

export default function Issues() {
  const nav = useNavigate();
  const { user } = useAuth();
  const staff = ["admin", "operations", "compliance"].includes(user.role);
  const [issues, setIssues] = useState([]);
  const [filter, setFilter] = useState("all");
  const load = () => api.get("/issues").then((r) => setIssues(r.data));
  useEffect(() => { load(); }, []);

  const del = async (iss) => {
    if (!window.confirm("Dismiss and permanently delete this issue?")) return;
    try { await api.delete(`/issues/${iss.id}`); toast.success("Issue dismissed"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const setStatus = async (iss, status) => {
    try { await api.patch(`/issues/${iss.id}`, { status }); toast.success(`${iss.title} → ${STATUS_META[status].label}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const assign = async (iss, name) => {
    try { await api.patch(`/issues/${iss.id}`, { assigned_to: name }); toast.success("Assigned"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const shown = issues.filter((i) => filter === "all" || i.status === filter);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="overline text-accent flex items-center gap-1.5"><Wrench className="h-3.5 w-3.5" />Maintenance</p>
          <h1 className="font-head text-3xl font-extrabold tracking-tight mt-1">Repair Issues</h1>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="issue-filter" className="w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
            </SelectContent>
          </Select>
          {staff && <NewIssueDialog onCreated={load} />}
        </div>
      </div>

      {shown.length === 0 ? (
        <div className="p-12 text-center bg-white border border-border rounded-md">
          <Wrench className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground">No repair issues to show.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {shown.map((iss) => {
            const sm = STATUS_META[iss.status];
            return (
              <div key={iss.id} data-testid={`issue-${iss.id}`} className="bg-white border border-border rounded-md p-4">
                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-md bg-secondary flex items-center justify-center shrink-0">
                    {iss.priority === "urgent" ? <AlertTriangle className="h-5 w-5 text-[#9E382A]" /> : <Wrench className="h-5 w-5" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium">{iss.title}</p>
                      <span className={`text-[0.6rem] px-2 py-0.5 rounded-full ${PRIORITY_CLS[iss.priority]}`}>{iss.priority}</span>
                      <span className="text-[0.6rem] px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">{iss.category}</span>
                      {iss.source === "public" && <span className="text-[0.6rem] px-2 py-0.5 rounded-full bg-[#D98324]/15 text-[#D98324]">Guest report</span>}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">{iss.description}</p>
                    <IssueExtras issue={iss} role={user.role} reload={load} />
                    <p className="text-xs text-muted-foreground mt-2">
                      <button className="underline hover:text-foreground" onClick={() => nav(`/properties/${iss.property_id}`)}>{iss.property_title}</button>
                      {" · "}Raised by {iss.raised_by_name} · {fmtDate(iss.created_at)}
                      {iss.assigned_to ? ` · Assigned: ${iss.assigned_to}` : ""}
                    </p>
                    {iss.updates?.length > 0 && (
                      <div className="mt-2 pl-3 border-l-2 border-border space-y-1">
                        {iss.updates.slice(-3).map((u, i) => (
                          <p key={i} className="text-xs text-muted-foreground">{u.text} — {u.by}</p>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 items-end shrink-0 w-40">
                    <span className={`text-xs px-2 py-1 rounded-full ${sm.cls}`}>{sm.label}</span>
                    <Select value={iss.status} onValueChange={(v) => setStatus(iss, v)}>
                      <SelectTrigger data-testid={`issue-status-${iss.id}`} className="h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="new">New</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="resolved">Resolved</SelectItem>
                      </SelectContent>
                    </Select>
                    {["admin", "operations"].includes(user.role) && <button data-testid={`issue-dismiss-${iss.id}`} onClick={() => del(iss)} className="text-xs text-[#9E382A] underline">Dismiss</button>}
                    <Input
                      data-testid={`issue-assign-${iss.id}`}
                      placeholder="Assign to…"
                      defaultValue={iss.assigned_to || ""}
                      onBlur={(e) => e.target.value !== (iss.assigned_to || "") && assign(iss, e.target.value)}
                      className="h-8 text-xs"
                    />
                    {staff && <ContractorDialog issue={iss} onDone={load} />}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
